import { instrumentRunQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient, type DownloadedFile } from '@/lib/api-client';
import type {
  InstrumentRunConsumablesPatch,
  InstrumentRunConsumablesUpdateResponse,
  InstrumentRunPublic,
  InstrumentRunSearchRequest,
  InstrumentRunSortField,
  PaginatedInstrumentRunsResponse,
  SortOrder,
} from '@/types';

/**
 * Fetch a single instrument run by ID
 *
 * @param orgId - The organization ID
 * @param runId - The instrument run ID
 * @returns Promise resolving to the instrument run data
 *
 * @throws {ApiError} If the request fails or the run is not found
 *
 * @example
 * ```typescript
 * const run = await fetchInstrumentRun('org-123', 'run-456');
 * console.log(run.name, run.status);
 * ```
 */
export async function fetchInstrumentRun(orgId: string, runId: string): Promise<InstrumentRunPublic> {
  const result = await apiClient.get<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.detail(orgId, runId), {
    expectJson: true,
  });

  if (result === null) {
    throw new Error(`Instrument run ${runId} not found`);
  }

  return result;
}

/**
 * Study-scoped run search. Same request/response contract as the org-scoped
 * `POST /orgs/{org_id}/instrument_runs/search`, narrowed to one study.
 */
export async function searchStudyInstrumentRuns(
  orgId: string,
  studyId: string,
  request: InstrumentRunSearchRequest,
): Promise<PaginatedInstrumentRunsResponse> {
  const result = await apiClient.post<PaginatedInstrumentRunsResponse>(
    studyQueryKeys.endpoints.instrumentRunsSearch(orgId, studyId),
    request,
    { expectJson: true },
  );

  if (result === null) throw new Error('searchStudyInstrumentRuns: endpoint returned no content');
  return result;
}

export interface StudyInstrumentRunsListParams {
  skip?: number;
  limit?: number;
  sortBy?: InstrumentRunSortField;
  sortOrder?: SortOrder;
}

/**
 * Study-scoped run list. Pagination and sorting only — use `searchStudyInstrumentRuns` when
 * filters are needed.
 */
export async function fetchStudyInstrumentRuns(
  orgId: string,
  studyId: string,
  params: StudyInstrumentRunsListParams = {},
): Promise<PaginatedInstrumentRunsResponse> {
  const { limit, skip, sortBy, sortOrder } = params;

  const result = await apiClient.get<PaginatedInstrumentRunsResponse>(
    studyQueryKeys.endpoints.instrumentRuns(orgId, studyId),
    { query: { skip, limit, sort_by: sortBy, sort_order: sortOrder }, expectJson: true },
  );

  if (result === null) throw new Error('fetchStudyInstrumentRuns: endpoint returned no content');
  return result;
}

/** Status-independent (unlike the draft-only PUT). Omit a field to leave it unchanged, send
 *  `null` to clear it. */
export async function updateInstrumentRunConsumables(
  orgId: string,
  runId: string,
  payload: InstrumentRunConsumablesPatch,
): Promise<InstrumentRunConsumablesUpdateResponse> {
  const result = await apiClient.patch<InstrumentRunConsumablesUpdateResponse>(
    instrumentRunQueryKeys.endpoints.consumables(orgId, runId),
    payload,
    { expectJson: true },
  );

  if (result === null) throw new Error('updateInstrumentRunConsumables: endpoint returned no content');
  return result;
}

/** Streams a run's sample list as CSV. */
export async function downloadInstrumentRunSamplesCsv(orgId: string, runId: string): Promise<DownloadedFile> {
  return apiClient.download(instrumentRunQueryKeys.endpoints.samplesDownload(orgId, runId));
}
